import { proposalRequestsMatchingPledge } from "@engine/round/accessors";
import type { RoundAggregate } from "@engine/round/aggregate";
import type NamedMatrix from "algorithm/NamedMatrix";
import { splitPortionAccounts, type FunderLedger } from "./funderLedger";
import type { SProcessOutput } from "./runSProcessOnRound";

/** First-layer phases are fixed contributions; second-layer phases are spending.
 * Conversion and unallocated money mean phase totals need not match across layers.
 * Ideal projections are available without assigning individual proposal payers. */
export type FundingPhases = Pick<
  FunderLedger,
  "byFunderRecommender" | "byRecommenderProposal"
>;

export function buildFundingPhases(
  round: RoundAggregate,
  output: SProcessOutput,
  standardAccounts: NamedMatrix<"recommender", "proposal+hold">,
  standardFunderAccounts: NamedMatrix<"funder", "recommender+hold">,
): FundingPhases {
  const result: FundingPhases = {
    byFunderRecommender: {},
    byRecommenderProposal: {},
  };
  const [funders, recommenders] = output.layerAccounts;
  const requesting = recommenders.colNames.map((id) => {
    const proposal = output.layerNodes[1].find((p) => p.id === id);
    return (
      proposal?.type === "proposal" && proposalRequestsMatchingPledge(proposal)
    );
  });

  for (const recommenderId of funders.colNames) {
    const inflows = funders.getColName(recommenderId).toArray();
    const matchingInflows = inflows.map((amount, f) =>
      Math.max(
        0,
        amount -
          standardFunderAccounts.getNames(funders.rowNames[f]!, recommenderId),
      ),
    );
    const totalMatching = matchingInflows.reduce(
      (sum, amount) => sum + amount,
      0,
    );
    const split =
      recommenderId === round.holdProposalId
        ? undefined
        : splitPortionAccounts(
            standardAccounts.getRowName(recommenderId).toArray(),
            recommenders.getRowName(recommenderId).toArray(),
            requesting,
            totalMatching,
          );

    if (split) {
      result.byRecommenderProposal[recommenderId] = Object.fromEntries(
        recommenders.colNames.map((id, p) => [id, split.amounts[p]!]),
      );
    }

    inflows.forEach((amount, f) => {
      // Contributions into the recommender keep their original phase split.
      // Downstream conversion or unallocated matching belongs to the ledger.
      const matching = matchingInflows[f]!;
      (result.byFunderRecommender[funders.rowNames[f]!] ??= {})[recommenderId] =
        {
          standard: amount - matching,
          matching,
        };
    });
  }

  return result;
}
